import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

const siteUrl = 'https://pipicinema.com';
const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  dianying: {
    title: '电影 - 高清电影在线免费观看 | 屁屁影院',
    description: '屁屁影院电影频道为您提供最新、最全的高清电影在线观看。涵盖动作片、喜剧片、科幻片、爱情片、恐怖片等多种类型，每日更新最新电影资源，让您畅享极致观影体验。',
    keywords: '电影在线观看,高清电影,最新电影,免费电影,动作电影,科幻电影,喜剧电影,屁屁影院',
  },
  dianshiju: {
    title: '电视剧 - 热门电视剧在线免费观看 | 屁屁影院',
    description: '屁屁影院电视剧频道汇集海量热门电视剧资源，包括国产剧、韩剧、美剧、日剧等。高清画质，流畅播放，每日同步更新最新剧集，追剧首选屁屁影院。',
    keywords: '电视剧在线观看,热门电视剧,国产剧,韩剧,美剧,最新电视剧,追剧,屁屁影院',
  },
  dongman: {
    title: '动漫 - 热门动漫在线免费观看 | 屁屁影院',
    description: '屁屁影院动漫频道为您提供最新最全的动漫作品在线观看，包括国产动漫、日本动漫、欧美动漫等。高清画质，流畅播放，每日更新最新番剧，动漫迷的最佳选择。',
    keywords: '动漫在线观看,国产动漫,日本动漫,新番动漫,动漫大全,动画片,屁屁影院',
  },
  zongyi: {
    title: '综艺 - 热门综艺节目在线免费观看 | 屁屁影院',
    description: '屁屁影院综艺频道汇集各类热门综艺节目，包括真人秀、音乐竞技、脱口秀、户外综艺等。高清画质，流畅播放，最新综艺节目尽在屁屁影院。',
    keywords: '综艺节目在线观看,热门综艺,真人秀,音乐节目,脱口秀,最新综艺,屁屁影院',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');
  const [showSort, setShowSort] = useState(false);

  const validSlug = slug && ['dianying', 'dianshiju', 'dongman', 'zongyi'].includes(slug) ? slug : 'dianying';

  const currentCategory = categories.find((c) => c.slug === validSlug);
  const meta = categoryMeta[validSlug];

  const filteredMovies: Movie[] = movies
    .filter((m) => m.category === validSlug)
    .filter((m) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        m.title.toLowerCase().includes(q) ||
        m.actors.some((a) => a.toLowerCase().includes(q)) ||
        m.tags.some((t) => t.toLowerCase().includes(q))
      );
    });

  const sortedMovies = [...filteredMovies].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'year') return b.year - a.year;
    return 0;
  });

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: meta.title,
    description: meta.description,
    url: `${siteUrl}/category/${validSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '屁屁影院',
      url: siteUrl,
    },
  };

  return (
    <>
      <PageMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`${siteUrl}/category/${validSlug}`}
        schema={schema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.label || '' },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="px-4 md:px-6 pb-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory?.label}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {filteredMovies.length} 部作品
                  {searchQuery && <span> - 搜索 &ldquo;{searchQuery}&rdquo; 的结果</span>}
                </p>
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowSort(!showSort)}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-background-200 bg-background-50 text-foreground-700 hover:border-secondary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-sort-desc"></i>
                  {sortBy === 'default' ? '默认排序' : sortBy === 'rating' ? '评分最高' : '最新发布'}
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                {showSort && (
                  <div className="absolute right-0 top-full mt-2 bg-background-50 border border-background-200/70 rounded-lg shadow-sm py-1 z-20 min-w-[140px]">
                    {[
                      { value: 'default' as const, label: '默认排序' },
                      { value: 'rating' as const, label: '评分最高' },
                      { value: 'year' as const, label: '最新发布' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => { setSortBy(opt.value); setShowSort(false); }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer ${
                          sortBy === opt.value ? 'text-primary-600 font-medium' : 'text-foreground-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    validSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200'
                  }`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="px-4 md:px-6 pb-14">
          <div className="max-w-7xl mx-auto">
            {sortedMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                {sortedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                  <i className="ri-search-line text-4xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">未找到相关影片</p>
                <Link
                  to={`/category/${validSlug}`}
                  className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  清除搜索条件
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}